/**
 * ClientBriefRoute
 *
 * Portal page for a single in-production brief, reached from the home
 * "In produzione" section. The brief is a navigation target (breadcrumb trail
 * Home > [Brief title]) rather than a drawer; tapping one of its approved
 * ideas opens a read-only modal over the page.
 */

import React, { useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { useBriefQuery } from "@/domains/Briefs/hooks/useBriefs";
import { BRIEF_STATUS_CONFIG } from "@/domains/Briefs/briefStatusConfig";
import { useDetailTrail } from "@/core/navigation/TrailDisplayContext";
import { useLocale } from "@/hooks/useLocale";
import { SpinnerIcon, ChevronRightIcon } from "@/components/icons";
import {
  ClientBriefIdeaModal,
  CONTENT_TYPE_IT,
} from "../components/ClientBriefIdeaModal";

interface ApprovalOutletContext {
  clientId: string;
  token?: string;
}

export const ClientBriefRoute: React.FC = () => {
  const { clientId, token } = useOutletContext<ApprovalOutletContext>();
  const { briefId } = useParams<{ briefId: string }>();
  const { t } = useLocale();
  const { data: brief, isLoading } = useBriefQuery(briefId);
  const [activeIdeaId, setActiveIdeaId] = useState<string | null>(null);

  // Publish "Home > [Brief title]" into the header trail. Runs before any
  // early return so hook order stays stable; falls back to the section title
  // until the brief resolves.
  useDetailTrail({
    fallback: { to: `/client/${clientId}`, label: t("client.trail.dashboard") },
    title: brief?.title?.trim() || t("portal.briefs.title"),
  });

  if (!token) {
    return (
      <div className="p-6 text-center text-gray-500">
        Please log in to view this brief
      </div>
    );
  }

  const status = brief ? BRIEF_STATUS_CONFIG[brief.status] : null;
  const ideas = brief?.ideas ?? [];
  const activeIdea = ideas.find((idea) => idea.id === activeIdeaId) ?? null;

  return (
    <div className="w-full max-w-2xl mx-auto px-4 lg:px-6 py-4 space-y-6">
      {isLoading && (
        <div className="flex justify-center py-12">
          <SpinnerIcon className="w-8 h-8 text-blue-600" />
        </div>
      )}

      {!isLoading && !brief && (
        <div className="p-6 text-center text-gray-500">
          Brief non trovato.
        </div>
      )}

      {!isLoading && brief && (
        <>
          <div className="bg-white rounded-2xl p-4">
            {status && (
              <span
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${status.bg} ${status.text}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                {status.label}
              </span>
            )}
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-gray-900 leading-tight">
              {brief.title || "Brief"}
            </h1>
            {brief.distilledContext && (
              <p className="mt-3 text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                {brief.distilledContext}
              </p>
            )}
          </div>

          <div className="bg-white rounded-2xl p-4">
            <h2 className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.12em]">
              Idee collegate
            </h2>

            {ideas.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">
                Nessuna idea collegata a questo brief.
              </p>
            ) : (
              <div className="mt-3 space-y-2">
                {ideas.map((idea) => (
                  <button
                    key={idea.id}
                    type="button"
                    onClick={() => setActiveIdeaId(idea.id)}
                    className="w-full flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors text-left group"
                  >
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-[0.1em]">
                        {idea.contentType
                          ? CONTENT_TYPE_IT[idea.contentType] || idea.contentType
                          : "Idea"}
                      </p>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {idea.title}
                      </p>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 shrink-0 text-gray-400 group-hover:text-gray-600 transition-colors" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <ClientBriefIdeaModal
        idea={activeIdea}
        isOpen={activeIdeaId !== null}
        onClose={() => setActiveIdeaId(null)}
      />
    </div>
  );
};
